<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useNotification } from '@kyvg/vue3-notification';
import StreamService from '@/services/StreamService';
import FormInput from '../Atoms/FormInput.vue';
import FormText from '../Atoms/FormText.vue';
import VODMetaTab from './VODMetaTab.vue';
import IconBase from '../icon/IconBase.vue';
import IconImage from '../icon/IconImage.vue';

const props = defineProps({
	vodFile: { type: Object, required: true },
	vodFolder: { type: Object, required: true },
	streamMeta: { type: Object, default: null },
	videoMeta: { type: Object, default: null },
	playbackUrl: { type: String, default: '' },
});

const emit = defineEmits(['update']);

const store = useStore();
const route = useRoute();
const { notify } = useNotification();

const title = ref('');
const description = ref('');

// Editing shows the clean name; the original extension is preserved on save so
// a display edit of "clip" never silently becomes "clip" without ".mp4".
const fileExt = computed(() => {
	const m = (props.vodFile?.fileName || '').match(/\.[a-z0-9]{2,4}$/i);
	return m ? m[0] : '';
});

const baseTitle = ref('');
const baseDescription = ref('');
const syncFromProps = () => {
	// Show the display name (no extension) — matches the editable header title.
	title.value = (props.vodFile?.fileName || '').replace(/\.[a-z0-9]{2,4}$/i, '');
	description.value = props.streamMeta?.videoMeta?.description || props.videoMeta?.description || '';
	baseTitle.value = title.value;
	baseDescription.value = description.value;
};
// Also re-sync when the filename changes (e.g. renamed from the header title).
watch(() => [props.vodFile?.id, props.vodFile?.fileName, props.streamMeta], syncFromProps, { immediate: true });

const saveTitle = async () => {
	const base = (title.value ?? '').trim().replace(/\.[a-z0-9]{2,4}$/i, '');
	if (!base) return;
	const newName = base + fileExt.value;
	if (newName === (props.vodFile.fileName || '')) return;
	if (props.vodFolder?.liveParent) {
		await StreamService.setStreamDvrArchiveName({
			streamId: props.vodFolder.liveParent,
			from: props.vodFile.archive.from,
			duration: props.vodFile.archive.duration,
			name: newName,
		});
	} else {
		await StreamService.updateStreamPlaylistVideoFilename(props.vodFolder._id, props.vodFile?.id, newName);
	}
};

const onBlurTitle = async () => {
	if (title.value === baseTitle.value) return;
	try {
		await saveTitle();
		await store.dispatch('Streams/getStreams');
		await store.dispatch('Streams/getFilesInVODFolder');
		baseTitle.value = title.value;
		emit('update');
	} catch (err) {
		notify({ group: 'error', text: err?.message || 'Could not rename video' });
	}
};

// Description autosaves on blur — consistent with the title field, so there's no
// separate "Save changes" button to hunt for at the bottom of the form.
const onBlurDescription = async () => {
	if (description.value === baseDescription.value) return;
	try {
		await StreamService.saveStreamMetadata(route.params.streamId, 'description', description.value, props.vodFile._id);
		StreamService.clearStreamMetadataCache(route.params.streamId);
		baseDescription.value = description.value;
		emit('update');
	} catch (err) {
		notify({ group: 'error', text: 'Could not save description' });
	}
};

</script>

<template>
	<div class="flex flex-col gap-4">
	<div class="rounded-lg ring-1 ring-surface-3 bg-surface-2 p-4 flex flex-col gap-4">
		<!-- Details group (title + description) — surface-1 inset like the embed panels -->
		<div class="flex flex-col gap-4 bg-surface-1 rounded-lg p-3">
			<!-- Title -->
			<div>
				<label class="text-sm font-medium text-surface-text-1 block mb-2">Title</label>
				<FormInput v-model="title" @blur="onBlurTitle" placeholder="Video title" customClasses="!bg-surface-3" />
			</div>

			<!-- Description -->
			<div>
				<label class="text-sm font-medium text-surface-text-1 block mb-2">Description</label>
				<FormText v-model="description" @blur="onBlurDescription" placeholder="Add a description"
					custom-classes="!bg-surface-3 !h-20 resize-none" />
			</div>
		</div>

		<!-- Media assets — always open (surface-1 inset like the embed groups) -->
		<div class="rounded-lg bg-surface-1 p-3">
			<div class="flex items-center gap-2 mb-3">
				<icon-base class="shrink-0 text-surface-8" fill="none" widthViewBox="24" heightViewBox="24">
					<icon-image />
				</icon-base>
				<span class="text-sm font-medium text-surface-text-1">Media assets</span>
			</div>
			<VODMetaTab media-only :vodFile="vodFile" :vodFolder="vodFolder" :stream-meta="streamMeta" />
		</div>
		</div>
		<!-- End main card -->
	</div>
</template>
